/**
 * Shared clock — BPM, beat number, beat-grid quantization. Distilled
 * from Air Drum Set's transport so the metronome and the quantize
 * scheduler agree on a single beat 0.
 *
 * Beat 0 is the AudioContext time captured the first time the
 * Transport is constructed; a BPM change re-anchors the clock so the
 * CURRENT beat position is preserved (no audible phase jump).
 */
export class Transport {
  bpm: number;
  /** Beats per bar (the numerator of the time signature). */
  quantum: number;
  /** Additive grouping that sums to `quantum`. Drives strong/medium/weak
   *  beat strength so the metronome accents group starts. */
  grouping: number[];

  private _epoch: number;
  private _epochBeat: number;
  private readonly ctx: AudioContext;

  constructor(
    ctx: AudioContext,
    bpm = 90,
    quantum = 4,
    grouping: number[] | null = null,
  ) {
    this.ctx = ctx;
    this.bpm = bpm;
    this.quantum = quantum;
    this.grouping = grouping && grouping.length ? grouping.slice() : [quantum];
    this._epoch = ctx.currentTime;
    this._epochBeat = 0;
  }

  audioTime(): number { return this.ctx.currentTime; }
  beatAt(t: number): number {
    return this._epochBeat + ((t - this._epoch) * this.bpm) / 60;
  }
  beatNow(): number { return this.beatAt(this.audioTime()); }
  timeAt(b: number): number {
    return this._epoch + ((b - this._epochBeat) * 60) / this.bpm;
  }
  phaseNow(): number {
    const b = this.beatNow();
    const q = this.quantum;
    return ((b % q) + q) % q;
  }

  setBpm(bpm: number): void {
    if (bpm === this.bpm || bpm <= 0) return;
    // Re-anchor so the current beat is preserved across the tempo change.
    const t = this.audioTime();
    const b = this.beatAt(t);
    this._epoch = t;
    this._epochBeat = b;
    this.bpm = bpm;
  }

  setTimeSignature(numerator: number, grouping?: number[] | null): void {
    const t = this.audioTime();
    const b = this.beatAt(t);
    this._epoch = t;
    this._epochBeat = b;
    this.quantum = numerator;
    this.grouping =
      grouping && grouping.length ? grouping.slice() : [numerator];
  }

  /** Beat strength inside a bar. Index 0 = strong, group starts = medium,
   *  rest = weak. */
  beatStrengthAt(beatIdx: number): 'strong' | 'medium' | 'weak' {
    const n = this.quantum || 1;
    const pos = ((beatIdx % n) + n) % n;
    let acc = 0;
    for (let g = 0; g < this.grouping.length; g++) {
      if (pos === acc) return g === 0 ? 'strong' : 'medium';
      acc += this.grouping[g]!;
      if (acc > pos) return 'weak';
    }
    return 'weak';
  }

  /** Quantize an audio time to the nearest grid division (in beats).
   *  1 = quarter, 0.5 = 8th, 0.25 = 16th, 0.125 = 32nd. */
  quantizeNearest(audioTime: number, division = 0.25): number {
    const beat = this.beatAt(audioTime);
    const nearest = Math.round(beat / division) * division;
    return this.timeAt(nearest);
  }

  quantizeUp(audioTime: number, division = 0.25): number {
    const beat = this.beatAt(audioTime);
    const next = Math.ceil(beat / division + 1e-6) * division;
    return this.timeAt(next);
  }
}
